import React from 'react';
import { ExternalLink, BarChart3, Users, MousePointerClick, LineChart as LineChartIcon, ShieldCheck } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { GA_MEASUREMENT_ID } from '../lib/analytics';

const GA_URL = `https://analytics.google.com/analytics/web/#/p${encodeURIComponent('?id=' + GA_MEASUREMENT_ID)}/reports/intelligenthome`;

const TRACKED_EVENTS = [
  { name: 'page_view', desc: 'Every route change (home, pricing, FAQ, dashboard, admin, etc.). Auto-fires.', icon: BarChart3 },
  { name: 'sign_up_click', desc: 'Sign Up Free buttons — Hero, CTA section, Navbar, Pricing.', icon: MousePointerClick, params: ['source'] },
  { name: 'login_click', desc: 'Log In button in Navbar + Continue with Google on /login.', icon: MousePointerClick, params: ['source', 'method'] },
  { name: 'alpaca_connect_click', desc: 'Connect Alpaca Paper button on /dashboard.', icon: MousePointerClick, params: ['authed', 'source'] },
  { name: 'signup_complete', desc: 'First-ever Google sign-in success (conversion).', icon: ShieldCheck, params: ['provider', 'is_admin'] },
  { name: 'login_complete', desc: 'Returning-user Google sign-in success.', icon: ShieldCheck, params: ['provider', 'is_admin'] },
  { name: 'alpaca_connected', desc: 'Alpaca OAuth callback succeeded. Fires on /alpaca/success.', icon: ShieldCheck, params: ['env'] },
  { name: 'beta_invite_click', desc: 'Recipient clicked magic-link in invite email. Fires on /invite page load.', icon: MousePointerClick, params: ['token_present'] },
  { name: 'beta_invite_verified', desc: 'Invite token verified — user is now a verified beta member (conversion).', icon: ShieldCheck, params: ['redirect_to'] },
  { name: 'beta_invite_failed', desc: 'Invite token rejected (expired/used/missing). Useful for spotting bad invites.', icon: MousePointerClick, params: ['detail'] },
  { name: 'checkout_click', desc: 'Pricing page CTA opens Stripe checkout (Stock Bot, Stock + Options Bot).', icon: MousePointerClick, params: ['plan', 'source'] },
];

export default function AdminAnalytics() {
  return (
    <AdminLayout
      title="Analytics"
      description="Google Analytics 4 is wired into talastradingco.com. All page views and key conversions stream to the GA4 property in real time."
    >
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 border border-white/10 rounded-md p-6 bg-[#0c0c0e]" data-testid="ga4-status-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">GA4 STATUS</p>
              <h2 className="font-display text-2xl font-bold text-white mt-1">Live in production</h2>
            </div>
            <span className="text-[10px] font-mono px-2.5 py-1 border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 rounded-sm">CONNECTED</span>
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] tracking-[0.25em] font-mono text-zinc-500">MEASUREMENT ID</p>
              <p className="mt-1 font-mono text-sm text-white">{GA_MEASUREMENT_ID}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] font-mono text-zinc-500">PROPERTY</p>
              <p className="mt-1 text-sm text-white">talastradingco.com</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://analytics.google.com/analytics/web/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ga4-open-realtime"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#d8b34a] text-black font-semibold text-sm rounded-sm hover:bg-[#e6c365] transition-colors"
            >
              Open Google Analytics
              <ExternalLink size={14} />
            </a>
            <a
              href={`https://analytics.google.com/analytics/web/#/p/reports/intelligenthome?params=_u..nav%3Dmaui`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-white/15 text-zinc-200 hover:border-[#d8b34a]/60 hover:text-white text-sm rounded-sm transition-colors"
            >
              Realtime report
              <ExternalLink size={14} />
            </a>
          </div>
          <p className="mt-5 text-xs text-zinc-500 leading-relaxed">
            GA4 needs ~10–30 seconds to show the first event after a real user visits. Use the Realtime report to confirm tracking is firing during a fresh deploy.
          </p>
        </div>

        <div className="border border-white/10 rounded-md p-6 bg-[#0c0c0e]">
          <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">WHAT'S TRACKED</p>
          <ul className="mt-4 space-y-3 text-xs text-zinc-300">
            <li className="flex items-start gap-2"><BarChart3 size={12} className="mt-0.5 gold-text" /><span>Every page view (SPA + cross-route)</span></li>
            <li className="flex items-start gap-2"><MousePointerClick size={12} className="mt-0.5 gold-text" /><span>Sign Up, Log In, Connect Alpaca clicks</span></li>
            <li className="flex items-start gap-2"><ShieldCheck size={12} className="mt-0.5 gold-text" /><span>Sign-up + login completions (conversions)</span></li>
            <li className="flex items-start gap-2"><LineChartIcon size={12} className="mt-0.5 gold-text" /><span>Beta invite click → verified conversion</span></li>
            <li className="flex items-start gap-2"><Users size={12} className="mt-0.5 gold-text" /><span>Logged-in users tagged with anonymous user_id</span></li>
          </ul>
          <p className="mt-5 text-[11px] text-zinc-500 leading-relaxed">
            No PII (emails, names, tokens) is sent to Google — only the user's internal anonymous id and a `role` tag.
          </p>
        </div>
      </div>

      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-white">Event reference</h3>
          <p className="text-[10px] tracking-[0.25em] font-mono text-zinc-500">{TRACKED_EVENTS.length} EVENTS</p>
        </div>
        <table className="w-full text-sm" data-testid="ga4-events-table">
          <thead className="border-b border-white/10 text-zinc-500 font-mono text-[10px] tracking-[0.3em] text-left">
            <tr>
              <th className="px-5 py-3">EVENT</th>
              <th className="px-5 py-3">WHEN IT FIRES</th>
              <th className="px-5 py-3">PARAMS</th>
            </tr>
          </thead>
          <tbody>
            {TRACKED_EVENTS.map((ev) => (
              <tr key={ev.name} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3 align-top">
                  <code className="font-mono text-xs text-white">{ev.name}</code>
                </td>
                <td className="px-5 py-3 align-top text-xs text-zinc-300 leading-relaxed max-w-xl">{ev.desc}</td>
                <td className="px-5 py-3 align-top">
                  <div className="flex flex-wrap gap-1.5">
                    {(ev.params || []).map((p) => (
                      <span key={p} className="text-[10px] font-mono text-zinc-400 px-1.5 py-0.5 border border-white/10 rounded-sm">{p}</span>
                    ))}
                    {!ev.params?.length && <span className="text-[10px] text-zinc-600">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 border border-amber-500/25 bg-amber-500/[0.04] rounded-md p-5">
        <p className="text-[10px] tracking-[0.3em] font-mono text-amber-300">SETUP HINT</p>
        <p className="mt-2 text-sm text-amber-100/90 leading-relaxed">
          For the cleanest funnel, mark <code className="font-mono text-amber-200">signup_complete</code>,{' '}
          <code className="font-mono text-amber-200">alpaca_connected</code>, and{' '}
          <code className="font-mono text-amber-200">beta_invite_verified</code> as <strong>conversions</strong> in GA4 (Admin → Events → toggle "Mark as conversion"). Once you do, you'll see them as goals in every report.
        </p>
      </div>
    </AdminLayout>
  );
}
